<script lang="ts">
    import { _ } from "svelte-i18n"
    import { getContent } from "../../api"
    import { currentLang, currentProjects } from "../../store"
    import Select from "svelte-select/Select.svelte"

    import ProjectTile from "./ProjectTile.svelte"

    export let content: Content
    let loading = true
    let projects: Content[]

    let selectedFilters: string[]
    $: selectedFilters = []
    $: filterList = []
    $: if (projects) {
        upateFilterList()
    }

    let innerWidth: number

    let dropdownFilterList = [
        {
            value: "none",
            label: $_("project.filter.all"),
        },
    ]

    let selectedDropdownFilterValue = null
    let filterUpdatePending: boolean = false
    let filteredProjectsList: any[] = []

    const selectFilterMobile = (selectedDropdownFilterValue) => {
        if (selectedDropdownFilterValue === undefined) {
            selectedFilters = []
        }
        if (!selectedDropdownFilterValue) return

        for (let i = 0; i < selectedDropdownFilterValue.length; i++) {
            if (selectedFilters.indexOf(selectedDropdownFilterValue[i]) == -1) {
                selectFilter(selectedDropdownFilterValue[i])
                selectedFilters = selectedFilters
                return // stop execution if condition is met
            }
        }

        for (let i = 0; i < selectedFilters.length; i++) {
            if (selectedDropdownFilterValue.indexOf(selectedFilters[i]) == -1) {
                selectFilter(selectedFilters[i])
                selectedFilters = selectedFilters
                return // stop execution if condition is met
            }
        }
        if (selectedDropdownFilterValue.length == 0) {
            filteredProjectsList = projects.map((p) => p.id)
        }
    }

    $: selectFilterMobile(selectedDropdownFilterValue?.map((o) => o.value))

    const selectFilter = (filter: string) => {
        if (filter == "none") {
            selectedFilters = []
        } else {
            // Remove "none" if it exists
            let i = selectedFilters.indexOf("none")
            if (i != -1) {
                selectedFilters.splice(i, 1)
            }

            // For single filter selection, replace existing filters
            selectedFilters = [filter]
        }

        selectedFilters = selectedFilters

        // Update URL
        let localizedFilter = selectedFilters
            .filter((filter) => filter !== "none")
            .map((filter) => {
                let localized = $_("project.categories." + filter)
                return localized
                    .toLowerCase()
                    .replaceAll("/", "-")
                    .replaceAll(" ", "")
                    .replaceAll("ö", "oe")
                    .replaceAll("ü", "ou")
                    .replaceAll("ä", "ae")
                    .replaceAll("ß", "ss")
            })
            .join(",")

        const urlPath = localizedFilter ? `/de/projekte/${localizedFilter}` : "/de/projekte"
        window.history.pushState({}, "", urlPath)
    }

    const loadProjects = () => {
        // Filter for API call - get content
        let apiParams: APIParams = {
            filter: {
                public: true,
                locale: $currentLang,
                type: "project",
            },
        }

        if (content?.projectSearchParameters?.region && content?.projectSearchParameters?.region !== "none") {
            apiParams.filter["project.general.region"] = content?.projectSearchParameters?.region
        }

        if (content?.projectSearchParameters?.date?.from || content?.projectSearchParameters?.date?.until) {
            apiParams.filter["project.general.date"] = {
                $gte: {
                    $date: new Date(content?.projectSearchParameters?.date?.from),
                },
                $lt: {
                    $date: new Date(content?.projectSearchParameters?.date?.until),
                },
            }
        }

        loading = true
        getContent(apiParams)
            .then((list) => {
                projects = list
                upateFilterList()
                generateDropdownFilterList()
            })
            .finally(() => {
                loading = false
            })
    }
    const upateFilterList = () => {
        let list = []
        if (projects && projects.length) {
            projects.forEach((p) => {
                if (!Array.isArray(p?.project?.general?.category)) {
                    p.project.general.category = [p?.project?.general?.category]
                }
                p?.project?.general?.category.forEach((cat) => {
                    if (!list.includes(cat)) {
                        list.push(cat)
                    }
                })
            })
        }

        // Parse URL to get category filter
        const pathParts = window.location.pathname.split("/")
        const categoryFromUrl = pathParts[pathParts.length - 1]

        // Convert URL category back to internal category name
        const getCategoryFromUrl = (urlCategory) => {
            if (!urlCategory || urlCategory === "projekte") return null

            // Handle multiple categories separated by comma
            const urlCategories = urlCategory.split(",")
            const allCategories = [
                "living",
                "renovation",
                "furniture-design",
                "urbanism",
                "culture-sports",
                "traffic-trade",
            ]
            const foundCategories = []

            for (const urlCat of urlCategories) {
                for (const category of allCategories) {
                    const localized = $_("project.categories." + category)
                        .toLowerCase()
                        .replaceAll("/", "-")
                        .replaceAll(" ", "")
                        .replaceAll("ö", "oe")
                        .replaceAll("ü", "ou")
                        .replaceAll("ä", "ae")
                        .replaceAll("ß", "ss")

                    if (localized === urlCat.trim()) {
                        foundCategories.push(category)
                        break
                    }
                }
            }

            return foundCategories.length > 0 ? foundCategories : null
        }

        const categoryFromUrlParsed = getCategoryFromUrl(categoryFromUrl)

        // Only set selectedFilters if they're not already set
        if (selectedFilters.length === 0) {
            if (categoryFromUrlParsed && Array.isArray(categoryFromUrlParsed)) {
                // Use categories from URL if they exist and are valid
                const validCategories = categoryFromUrlParsed.filter((cat) => list.includes(cat))
                if (validCategories.length > 0) {
                    selectedFilters = validCategories
                } else {
                    selectedFilters = ["none"]
                }
            } else if (
                content.projectSearchParameters.category !== "none" &&
                list.includes(content.projectSearchParameters.category)
            ) {
                selectedFilters = [content.projectSearchParameters.category]
            } else {
                selectedFilters = ["none"]
            }
        }

        filterList = list
    }

    const generateDropdownFilterList = () => {
        const filters = [
            {
                value: "none",
                label: $_("project.filter.all"),
            },
        ]
        selectedDropdownFilterValue = filters.filter((filter) => filter.value === selectedFilters[0]) || filters

        dropdownFilterList = [
            ...filters,
            ...filterList.map((filter) => {
                return {
                    value: filter,
                    label: $_("project.categories." + filter),
                }
            }),
        ]
    }

    const getRegion = () => {
        if (content?.projectSearchParameters?.region && content?.projectSearchParameters?.region !== "none") {
            return $_("project.region." + content?.projectSearchParameters?.region)
        } else {
            return ""
        }
    }

    const getTime = () => {
        if (content?.projectSearchParameters?.date?.from || content?.projectSearchParameters?.date?.until) {
            const from = content?.projectSearchParameters?.date?.from
                ? formatDate(content?.projectSearchParameters?.date?.from) + " - "
                : ""
            const until = content?.projectSearchParameters?.date?.until
                ? formatDate(content?.projectSearchParameters?.date?.until)
                : ""
            return from + until
        }

        return ""
    }

    const formatDate = (date) => {
        const event = new Date(date)
        return event.toLocaleDateString($currentLang, { year: "numeric" })
    }

    $: if (content) loadProjects()

    // Initialize filters from URL when projects are loaded
    $: if (projects && projects.length > 0 && selectedFilters.length === 0) {
        const pathParts = window.location.pathname.split("/")
        const categoryFromUrl = pathParts[pathParts.length - 1]

        if (categoryFromUrl && categoryFromUrl !== "projekte") {
            const allCategories = [
                "living",
                "renovation",
                "furniture-design",
                "urbanism",
                "culture-sports",
                "traffic-trade",
            ]

            // Handle multiple categories separated by comma
            const urlCategories = categoryFromUrl.split(",")
            const foundCategories = []

            for (const urlCat of urlCategories) {
                for (const category of allCategories) {
                    const localized = $_("project.categories." + category)
                        .toLowerCase()
                        .replaceAll("/", "-")
                        .replaceAll(" ", "")
                        .replaceAll("ö", "oe")
                        .replaceAll("ü", "ou")
                        .replaceAll("ä", "ae")
                        .replaceAll("ß", "ss")

                    if (localized === urlCat.trim()) {
                        foundCategories.push(category)
                        break
                    }
                }
            }

            if (foundCategories.length > 0) {
                selectedFilters = foundCategories
            }
        }
    }

    $: if (selectedFilters.length == 0) {
        setTimeout(() => {
            filteredProjectsList = projects.map((p) => p.id)
            filterUpdatePending = false
        }, 1000)
    }
    $: if (selectedFilters.length) {
        filterUpdatePending = true
        setTimeout(() => {
            filteredProjectsList = []
        }, 1000)

        setTimeout(() => {
            filteredProjectsList = projects
                ?.filter((p) => {
                    // If no filters or only "none" filter, show all projects
                    if (
                        selectedFilters.length === 0 ||
                        (selectedFilters.length === 1 && selectedFilters[0] === "none")
                    ) {
                        return true
                    }

                    // Check if project matches any of the selected filters
                    if (!Array.isArray(p?.project?.general?.category)) {
                        p.project.general.category = [p?.project?.general?.category]
                    }

                    return selectedFilters.some((filter) => {
                        if (filter === "none") return true
                        else if (filter && p?.project?.general?.category.some((cat) => cat === filter)) return true
                        return false
                    })
                })
                .map((p) => p.id)
            filterUpdatePending = false
        }, 1000)
    }

    $: $currentProjects = projects
</script>

<svelte:window bind:innerWidth />

{#if content}
    <section class="projects-list">
        <div class="projects-list-page-header">
            <h1>
                {$_("projects")}
                <span class="project-filter-type">
                    {getRegion()}
                    {getTime()}
                </span>
            </h1>

            <section class="projects-list-filter">
                {#if innerWidth > 992}
                    <nav>
                        <div
                            on:click="{() => (selectedFilters = [])}"
                            class:active="{!selectedFilters.length}"
                            on:keydown
                        >
                            {$_("project.filter.all")}
                        </div>

                        {#each filterList || [] as filter}
                            <div
                                on:click="{() => selectFilter(filter)}"
                                class:active="{selectedFilters.includes(filter)}"
                                on:keydown
                            >
                                {$_("project.categories." + filter)}
                            </div>
                        {:else}
                            {#if content.projectSearchParameters.category !== "none"}
                                {#if Array.isArray(content.projectSearchParameters.category)}
                                    {#each content.projectSearchParameters.category as filter}
                                        <div>{$_("project.categories." + filter)}</div>
                                    {/each}
                                {:else}
                                    <div>{$_("project.categories." + content.projectSearchParameters.category)}</div>
                                {/if}
                            {/if}
                        {/each}
                    </nav>
                {:else}
                    <Select
                        items="{dropdownFilterList}"
                        hideEmptyState="{true}"
                        multiple="{true}"
                        bind:value="{selectedDropdownFilterValue}"
                        clearable="{false}"
                    />
                {/if}
            </section>
        </div>

        <section class="project-tiles" class:show="{!filterUpdatePending}">
            {#each projects || [] as p, i}
                {#if filteredProjectsList.includes(p.id)}
                    <ProjectTile project="{p}" show="{true}" />
                {/if}
            {:else}
                <div>{$_("project.not-found")}</div>
            {/each}
        </section>
    </section>
{/if}
